/**
 * Sistema robusto de tratamento de erros para capturar e gerenciar
 * erros relacionados a extensões do navegador e comunicação assíncrona
 */

class ErrorHandler {
  constructor() {
    this.errorQueue = [];
    this.isProcessing = false;
    this.debounceTimers = new Map();
    this.errorCounts = new Map();
    this.maxErrorsPerType = 5;
    this.resetInterval = 60000; // 1 minuto

    // Inicializar tratamento de erros
    this.init();
  }

  init() {
    // Capturar erros globais não tratados
    window.addEventListener('error', this.handleGlobalError.bind(this));

    // Capturar promises rejeitadas não tratadas
    window.addEventListener(
      'unhandledrejection',
      this.handleUnhandledRejection.bind(this)
    );

    // Resetar contadores de erro periodicamente
    setInterval(() => {
      this.errorCounts.clear();
    }, this.resetInterval);
  }

  /**
   * Manipular erros globais
   */
  handleGlobalError(event) {
    const error = {
      type: 'global_error',
      message: event.message,
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      error: event.error,
      timestamp: Date.now(),
    };

    this.processError(error);
  }

  /**
   * Manipular promises rejeitadas não tratadas
   */
  handleUnhandledRejection(event) {
    const error = {
      type: 'unhandled_rejection',
      message: event.reason?.message || 'Promise rejection',
      reason: event.reason,
      timestamp: Date.now(),
    };

    // Verificar se é erro de message channel (extensões)
    if (this.isMessageChannelError(event.reason)) {
      error.type = 'message_channel_error';
      error.isExtensionRelated = true;

      // Prevenir que o erro apareça no console para erros de extensão
      event.preventDefault();

      // Log silencioso para debugging
      console.debug(
        '🔇 Erro de extensão do navegador capturado e silenciado:',
        error.message
      );
      return;
    }

    this.processError(error);
  }

  /**
   * Verificar se é um erro relacionado a message channel (extensões)
   */
  // eslint-disable-next-line class-methods-use-this
  isMessageChannelError(error) {
    if (!error || typeof error !== 'object') return false;

    const messageChannelPatterns = [
      'message channel closed',
      'listener indicated an asynchronous response',
      'Extension context invalidated',
      'chrome-extension://',
      'moz-extension://',
      'safari-extension://',
      'The message port closed before a response was received',
    ];

    const errorMessage = error.message || error.toString() || '';
    return messageChannelPatterns.some((pattern) =>
      errorMessage.toLowerCase().includes(pattern.toLowerCase())
    );
  }

  /**
   * Processar erro com debounce para evitar spam
   */
  processError(error) {
    const errorKey = `${error.type}_${error.message}`;

    // Verificar se já processamos muitos erros deste tipo
    const currentCount = this.errorCounts.get(errorKey) || 0;
    if (currentCount >= this.maxErrorsPerType) {
      return; // Ignorar erros excessivos do mesmo tipo
    }

    this.errorCounts.set(errorKey, currentCount + 1);

    // Debounce para evitar processamento excessivo
    if (this.debounceTimers.has(errorKey)) {
      clearTimeout(this.debounceTimers.get(errorKey));
    }

    const timer = setTimeout(() => {
      this.addToQueue(error);
      this.debounceTimers.delete(errorKey);
    }, 100);

    this.debounceTimers.set(errorKey, timer);
  }

  /**
   * Adicionar erro à fila de processamento
   */
  addToQueue(error) {
    this.errorQueue.push(error);

    // Limitar tamanho da fila
    if (this.errorQueue.length > 50) {
      this.errorQueue = this.errorQueue.slice(-25);
    }

    if (!this.isProcessing) {
      this.processQueue();
    }
  }

  /**
   * Processar fila de erros
   */
  async processQueue() {
    if (this.isProcessing || this.errorQueue.length === 0) return;

    this.isProcessing = true;

    // Processar todos os erros em paralelo para evitar await-in-loop
    const errors = [...this.errorQueue];
    this.errorQueue = [];

    try {
      await Promise.all(errors.map((error) => this.handleError(error)));
    } catch (e) {
      console.error('Erro ao processar fila de erros:', e);
    }

    this.isProcessing = false;
  }

  /**
   * Manipular erro individual
   */
  async handleError(error) {
    try {
      // Log estruturado para debugging
      if (process.env.NODE_ENV === 'development') {
        console.group(`🚨 Erro capturado: ${error.type}`);
        console.log('Mensagem:', error.message);
        console.log('Timestamp:', new Date(error.timestamp).toISOString());
        console.log('Detalhes:', error);
        console.groupEnd();
      }

      // Enviar erro para serviço de logging se configurado
      if (this.shouldReportError(error)) {
        await this.reportError(error);
      }
    } catch (reportError) {
      console.error('Erro ao processar erro:', reportError);
    }
  }

  /**
   * Verificar se deve reportar o erro
   */
  shouldReportError(error) {
    // Não reportar erros de extensões
    if (error.isExtensionRelated) return false;

    // Não reportar erros de desenvolvimento
    if (process.env.NODE_ENV === 'development') return false;

    // Não reportar erros muito frequentes
    const errorKey = `${error.type}_${error.message}`;
    const count = this.errorCounts.get(errorKey) || 0;
    return count <= 2;
  }

  /**
   * Reportar erro para serviço de logging
   */
  // eslint-disable-next-line class-methods-use-this
  async reportError(error) {
    try {
      // Implementar integração com serviço de logging se necessário
      // Por exemplo: Sentry, LogRocket, etc.
      console.log('📊 Erro reportado para logging:', error);
    } catch (e) {
      console.error('Falha ao reportar erro:', e);
    }
  }

  /**
   * Criar wrapper seguro para event listeners
   */
  safeEventListener(callback, context = null) {
    return (...args) => {
      try {
        if (context) {
          return callback.apply(context, args);
        }
        return callback(...args);
      } catch (error) {
        this.processError({
          type: 'event_listener_error',
          message: error.message,
          stack: error.stack,
          timestamp: Date.now(),
          callback: callback.name || 'anonymous',
        });
        return undefined;
      }
    };
  }

  /**
   * Criar wrapper seguro para promises
   */
  safePromise(promise) {
    return promise.catch((error) => {
      if (!this.isMessageChannelError(error)) {
        this.processError({
          type: 'promise_error',
          message: error.message,
          stack: error.stack,
          timestamp: Date.now(),
        });
      }
      throw error; // Re-throw para manter comportamento original
    });
  }

  /**
   * Limpar recursos
   */
  cleanup() {
    // Limpar timers de debounce
    this.debounceTimers.forEach((timer) => clearTimeout(timer));
    this.debounceTimers.clear();

    // Limpar fila de erros
    this.errorQueue = [];

    // Remover event listeners
    window.removeEventListener('error', this.handleGlobalError.bind(this));
    window.removeEventListener(
      'unhandledrejection',
      this.handleUnhandledRejection.bind(this)
    );
  }

  /**
   * Obter estatísticas de erros
   */
  getErrorStats() {
    return {
      queueSize: this.errorQueue.length,
      errorCounts: Object.fromEntries(this.errorCounts),
      isProcessing: this.isProcessing,
    };
  }
}

// Criar instância global
const errorHandler = new ErrorHandler();

// Exportar instância e classe
export default errorHandler;
export { ErrorHandler };
